import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/core/Logo';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState('Pro');
  const pollAttemptsRef = useRef(0);
  const maxAttempts = 10;

  useEffect(() => {
    const pollPaymentStatus = async (sessionId) => {
      if (pollAttemptsRef.current >= maxAttempts) {
        // Max attempts reached, but might have succeeded via webhook
        await refreshUser();
        setStatus('success');
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid' || data.status === 'complete') {
          await refreshUser();
          setStatus('success');
          setPlan(data.plan === 'MAX' ? 'Max' : 'Pro');
          toast.success(`Welcome to Atlas ${data.plan === 'MAX' ? 'Max' : 'Pro'}!`);
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          setError('Payment session expired. Please try again.');
          return;
        }

        // Payment still pending, poll again
        pollAttemptsRef.current++;
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      } catch (err) {
        console.error('Status check error:', err);
        // Even on error, refresh user as webhook might have processed
        pollAttemptsRef.current++;
        if (pollAttemptsRef.current >= maxAttempts) {
          await refreshUser();
          setStatus('success');
        } else {
          setTimeout(() => pollPaymentStatus(sessionId), 2000);
        }
      }
    };

    const verifyCheckout = async () => {
      const sessionId = searchParams.get('session_id');
      const planParam = searchParams.get('plan');
      
      if (planParam) {
        setPlan(planParam === 'MAX' ? 'Max' : 'Pro');
      }
      
      if (!sessionId) {
        // No session ID but user might already be upgraded via webhook
        await refreshUser();
        setStatus('success');
        return;
      }

      // Start polling for payment status
      pollPaymentStatus(sessionId);
    };

    verifyCheckout();
  }, [searchParams, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="border-b border-border px-6 h-14 flex items-center">
        <Logo />
      </nav>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          {status === 'verifying' && (
            <div className="animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-cobalt animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">Verifying Payment</h1>
              <p className="font-body text-muted-foreground">Please wait while we confirm your subscription...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">Welcome to Atlas {plan}!</h1>
              <p className="font-body text-muted-foreground mb-6">
                Your subscription is now active. Enjoy unlimited mind maps, exports, and AI features.
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-body font-medium text-sm mb-2">What's included:</h3>
                <ul className="font-body text-sm text-muted-foreground space-y-1">
                  <li>• {plan === 'Max' ? '150' : '50'} AI credits/month</li>
                  <li>• Unlimited Mind Maps</li>
                  <li>• {plan === 'Max' ? 'Unlimited depth' : '3-level deep'} mind maps</li>
                  <li>• Export to PNG & PDF</li>
                  <li>• Priority Support</li>
                </ul>
              </div>

              <Button 
                onClick={() => navigate('/dashboard')} 
                className="w-full bg-cobalt text-white hover:bg-cobalt/90"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">Something Went Wrong</h1>
              <p className="font-body text-muted-foreground mb-6">
                {error || "We couldn't verify your payment. Please contact support if you were charged."}
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')} 
                  className="flex-1"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  onClick={() => window.location.reload()} 
                  className="flex-1 bg-cobalt text-white hover:bg-cobalt/90"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
